import { z } from 'zod';

export const PROMOTION_TYPES = ['percentage', 'fixed_amount', 'free_shipping', 'buy_x_get_y', 'bundle'] as const;

export const createPromotionSchema = z.object({
  name: z
    .string()
    .min(1, 'validation.required')
    .min(3, 'validation.minLength')
    .max(100, 'validation.maxLength'),
  displayName: z
    .string()
    .max(100, 'validation.maxLength')
    .optional()
    .or(z.literal('')),
  description: z
    .string()
    .max(500, 'validation.maxLength')
    .optional()
    .or(z.literal('')),
  type: z.enum(PROMOTION_TYPES, {
    message: 'validation.required',
  }),
  value: z
    .number({ message: 'validation.number' })
    .min(0, 'validation.positiveNumber'),
  // Buy X Get Y fields
  buyQuantity: z
    .number()
    .int()
    .min(1, 'validation.positiveNumber')
    .optional(),
  getQuantity: z
    .number()
    .int()
    .min(1, 'validation.positiveNumber')
    .optional(),
  // Minimum requirements
  minPurchaseAmount: z
    .number()
    .min(0, 'validation.positiveNumber')
    .optional(),
  minQuantity: z
    .number()
    .int()
    .min(1, 'validation.positiveNumber')
    .optional(),
  // Usage limits
  maxUsage: z
    .number()
    .int()
    .min(1, 'validation.positiveNumber')
    .optional(),
  maxUsagePerCustomer: z
    .number()
    .int()
    .min(1, 'validation.positiveNumber')
    .optional(),
  // Period
  startDate: z.string().min(1, 'validation.required'),
  endDate: z.string().min(1, 'validation.required'),
  // Priority and stacking
  priority: z
    .number()
    .int()
    .min(0, 'validation.positiveNumber')
    .optional(),
  stackable: z.boolean().optional(),
  // Application scope
  applyToAllProducts: z.boolean().optional(),
  // Visuals
  badgeColor: z.string().optional(),
  // Internal
  internalNotes: z.string().max(1000, 'validation.maxLength').optional(),
  // Relations
  shopRefIds: z.array(z.string()).optional(),
  productRefIds: z.array(z.string()).optional(),
  categoryIds: z.array(z.number()).optional(),
}).refine(
  (data) => {
    // For buy_x_get_y, both buyQuantity and getQuantity are required
    if (data.type === 'buy_x_get_y') {
      return data.buyQuantity && data.buyQuantity > 0 && data.getQuantity && data.getQuantity > 0;
    }
    return true;
  },
  {
    message: 'validation.buyXGetYRequired',
    path: ['buyQuantity'],
  }
).refine(
  (data) => {
    // For percentage, value should be between 0 and 100
    if (data.type === 'percentage') {
      return data.value >= 0 && data.value <= 100;
    }
    return true;
  },
  {
    message: 'validation.percentageRange',
    path: ['value'],
  }
).refine(
  (data) => {
    // End date should be after start date
    if (data.startDate && data.endDate) {
      return new Date(data.endDate) > new Date(data.startDate);
    }
    return true;
  },
  {
    message: 'validation.endDateAfterStart',
    path: ['endDate'],
  }
);

export type CreatePromotionFormData = z.infer<typeof createPromotionSchema>;
